import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode,
} from 'react';
import { Platform, Pressable, StyleSheet, type ViewStyle } from 'react-native';
import { DatePicker as WheelDatePicker } from 'react-native-wheel-pick';

import { Stack } from '@/components/layout';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/ui/modal';
import { SvgIcon } from '@/components/ui/svg-icon';
import { Text } from '@/components/ui/text';
import type { TextInputStatus } from '@/components/ui/text-input/types';
import { createStyles, useScaling } from '@/hooks';
import { colors } from '@/theme/colors';
import type { TypographyToken } from '@/theme/typography';
import { DatePickerSelectFields } from './date-picker-select-fields';

type WheelDatePickerProps = {
  style?: ViewStyle;
  date?: Date;
  minimumDate?: Date;
  maximumDate?: Date;
  onDateChange?: (date: Date) => void;
};

const DatePickerComponent = WheelDatePicker as unknown as FC<WheelDatePickerProps>;

export type DateTimePickerProps = {
  value?: Date | null;
  defaultValue?: Date | null;
  onDateChange?: (date: Date) => void;
  minimumDate?: Date;
  maximumDate?: Date;
  placeholder?: string;
  disabled?: boolean;
  status?: Exclude<TextInputStatus, 'disabled'>;
  helperText?: ReactNode;
  style?: ViewStyle | ViewStyle[];
  triggerStyle?: ViewStyle | ViewStyle[];
  displayVariant?: TypographyToken;
  displayFormat?: (date: Date) => string;
};

const formatDisplayDate = (date: Date) => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

export const DateTimePicker: FC<DateTimePickerProps> = memo(
  ({
    value,
    defaultValue = null,
    onDateChange,
    minimumDate,
    maximumDate,
    placeholder = 'Select date',
    disabled = false,
    status = 'default',
    helperText,
    style,
    triggerStyle,
    displayVariant = 'Font Inter/Subtitle/Regular 14px',
    displayFormat = formatDisplayDate,
  }) => {
    const [open, setOpen] = useState(false);
    const [pickerSession, setPickerSession] = useState(0);
    const [internalDate, setInternalDate] = useState<Date | null>(defaultValue);
    const styles = useStyles();
    const { scale, deviceWidth, isTablet } = useScaling();
    const changeDate = useRef<Date | null>(value ?? internalDate);

    const isControlled = value !== undefined;
    const selectedDate = isControlled ? (value ?? null) : internalDate;

    const displayLabel = useMemo(
      () => (selectedDate ? displayFormat(selectedDate) : placeholder),
      [displayFormat, placeholder, selectedDate],
    );
    const isPlaceholder = selectedDate == null;

    const handleDateChange = useCallback(
      (next: Date) => {
        if (!(next instanceof Date) || Number.isNaN(next.getTime())) return;

        // Some pickers return calendar day as 00:00:00.000Z. Local getters then shift the calendar
        // day west of UTC (e.g. America/Anchorage). Use UTC components only for that encoding.
        const isUtcMidnight =
          next.getUTCHours() === 0 &&
          next.getUTCMinutes() === 0 &&
          next.getUTCSeconds() === 0 &&
          next.getUTCMilliseconds() === 0;

        const y = isUtcMidnight ? next.getUTCFullYear() : next.getFullYear();
        const m = isUtcMidnight ? next.getUTCMonth() : next.getMonth();
        const d = isUtcMidnight ? next.getUTCDate() : next.getDate();

        let normalizedNextDate = new Date(y, m, d);
        if (Number.isNaN(normalizedNextDate.getTime())) return;

        const offsetMinutes = normalizedNextDate.getTimezoneOffset();
        if (offsetMinutes <= 0) {
          normalizedNextDate = new Date(
            normalizedNextDate.getTime() + offsetMinutes * 60 * 1000 * -1,
          );
        }

        if (!isControlled) setInternalDate(normalizedNextDate);
        changeDate.current = normalizedNextDate;
      },
      [isControlled],
    );

    const onConfirm = useCallback(() => {
      if (changeDate.current) {
        onDateChange?.(changeDate.current);
        setOpen(false);
      }
    }, [onDateChange]);

    const toggleOpen = () => {
      if (disabled) return;
      setOpen(prev => {
        const next = !prev;
        if (next) setPickerSession(s => s + 1);
        return next;
      });
    };

    useEffect(() => {
      if (selectedDate) {
        changeDate.current = selectedDate;
      }
    }, [selectedDate]);

    return (
      <Stack style={[styles.root, style]}>
        {open ? <Pressable style={styles.backdrop} onPress={() => setOpen(false)} /> : null}

        <Pressable
          accessibilityRole="button"
          accessibilityState={{ expanded: open, disabled }}
          disabled={disabled}
          onPress={toggleOpen}
          style={[
            styles.trigger,
            status === 'error' ? styles.triggerError : undefined,
            disabled ? styles.disabled : undefined,
            triggerStyle,
          ]}>
          <Text
            variant={displayVariant}
            style={[
              styles.triggerLabel,
              isPlaceholder ? styles.triggerPlaceholderLabel : undefined,
            ]}
            numberOfLines={1}>
            {displayLabel}
          </Text>

          <Stack style={styles.iconWrapper}>
            <SvgIcon name="IcCalendar" size={scale(16)} color={colors['Blue 2']} />
          </Stack>
        </Pressable>

        {open ? (
          <Modal visible={open} onBackdropPress={() => setOpen(false)}>
            <Stack
              padding={scale(12)}
              borderRadius={scale(12)}
              minWidth={Platform.select({
                android: !isTablet ? deviceWidth - scale(24) : scale(380),
                ios: undefined,
                web: !isTablet ? Math.min(deviceWidth - scale(24), scale(420)) : scale(420),
                default: scale(320),
              })}
              backgroundColor={'white'}>
              {Platform.OS === 'web' ? (
                <DatePickerSelectFields
                  key={pickerSession}
                  date={selectedDate ?? undefined}
                  minimumDate={minimumDate}
                  maximumDate={maximumDate ?? new Date('2199-12-31')}
                  onDateChange={handleDateChange}
                />
              ) : (
                <DatePickerComponent
                  style={styles.wheel}
                  date={selectedDate ?? undefined}
                  minimumDate={minimumDate}
                  maximumDate={maximumDate ?? new Date('2199-12-31')}
                  onDateChange={handleDateChange}
                />
              )}
              <Stack direction="row" justify="flex-end" gap={scale(12)}>
                <Button variant="ghost" onPress={onConfirm}>
                  <Button.Text>Confirm</Button.Text>
                </Button>
                <Button variant="ghost" onPress={() => setOpen(false)}>
                  <Button.Text style={{ color: colors.Error }}>Cancel</Button.Text>
                </Button>
              </Stack>
            </Stack>
          </Modal>
        ) : null}
        {helperText ? (
          <Text
            variant="Font Inter/Subtitle/Regular 11px"
            style={[styles.helperText, status === 'error' ? styles.helperTextError : undefined]}>
            {helperText}
          </Text>
        ) : null}
      </Stack>
    );
  },
);

DateTimePicker.displayName = 'DateTimePicker';

const useStyles = createStyles(({ scale }) => ({
  root: {
    position: 'relative',
    zIndex: 20,
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'transparent',
    zIndex: 1,
  },
  trigger: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: scale(1),
    borderRadius: scale(4),
    borderColor: colors['5'],
    backgroundColor: colors['Backgrounds/Primary'],
    paddingHorizontal: scale(12),
    height: scale({
      phone: 44,
      tablet: 48,
    }),
    zIndex: 2,
  },
  triggerError: {
    borderColor: colors.Error,
  },
  triggerLabel: {
    flex: 1,
    color: colors['Labels/Primary'],
    marginRight: scale(12),
  },
  triggerPlaceholderLabel: {
    color: colors['5'],
  },
  iconWrapper: {
    width: scale(20),
    height: scale(20),
    alignItems: 'center',
    justifyContent: 'center',
  },
  contentWrapper: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: scale(8),
    zIndex: 3,
    backgroundColor: colors['Backgrounds/Primary'],
    borderRadius: scale(4),
    borderWidth: scale(1),
    borderColor: colors['5'],
    overflow: 'hidden',
    paddingHorizontal: scale(12),
    paddingVertical: scale(8),
  },
  disabled: {
    opacity: 0.6,
  },
  helperText: {
    marginTop: scale(6),
    color: colors['Labels/Secondary'],
  },
  helperTextError: {
    color: colors.Error,
  },
  wheel: {
    width: '100%',
    height: scale(220),
    backgroundColor: colors['Backgrounds/Primary'],
  },
}));
